import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Select,
  DatePicker,
  Input,
  Tag,
  Tooltip,
  Space,
  Badge,
  Drawer,
  Divider,
  Typography,
  Row,
  Col,
  message,
  Card,
} from "antd";
import {
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClearOutlined,
  SaveOutlined,
  ExportOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import {
  mockWorkOrderAmendmentData,
  getWorkOrdersInChangeRequest,
  getChangeRequestStatusColor,
  WorkOrderAmendmentData,
  ChangeRequestStatus,
} from "./mockData/workOrderAmendmentData";
import {
  mockSiteOptions,
  mockProjectOptions,
  mockContractTypeOptions,
} from "./mockData/pendingWorkOrdersData";
import WorkOrderAside from "./workOrderAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const WorkOrderAmendments: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkOrderAmendmentData[]>([]);
  const [filteredData, setFilteredData] = useState<WorkOrderAmendmentData[]>(
    []
  );
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");

  // Filter states
  const [filters, setFilters] = useState({
    site: "",
    project: "",
    status: "",
    contractType: "",
    contractor: "",
    valueRange: { min: "", max: "" },
    dateRange: null as any,
    globalSearch: "",
  });

  // Drawer states
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [savedFilters, setSavedFilters] = useState<any[]>([]);

  useEffect(() => {
    loadWorkOrderAmendments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const loadWorkOrderAmendments = async () => {
    try {
      setLoading(true);
      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      const amendmentData = getWorkOrdersInChangeRequest();
      setData(amendmentData);
    } catch (error) {
      console.error("Error loading work order amendments:", error);
      message.error("Failed to load work order amendments");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (filters.globalSearch) {
      const searchTerm = filters.globalSearch.toLowerCase();
      filtered = filtered.filter(
        (wo: any) =>
          wo.workOrderNumber.toLowerCase().includes(searchTerm) ||
          wo.workOrderTitle.toLowerCase().includes(searchTerm) ||
          wo.contractorName.toLowerCase().includes(searchTerm) ||
          wo.siteName.toLowerCase().includes(searchTerm) ||
          wo.projectName.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.site) {
      filtered = filtered.filter((wo) => wo.siteId === filters.site);
    }
    if (filters.project) {
      filtered = filtered.filter((wo) => wo.projectId === filters.project);
    }
    if (filters.status) {
      filtered = filtered.filter(
        (wo) => wo.changeRequestStatus === filters.status
      );
    }
    if (filters.contractType) {
      filtered = filtered.filter(
        (wo) => wo.contractType === filters.contractType
      );
    }
    if (filters.contractor) {
      filtered = filtered.filter((wo) =>
        wo.contractorName
          .toLowerCase()
          .includes(filters.contractor.toLowerCase())
      );
    }
    if (filters.valueRange.min || filters.valueRange.max) {
      filtered = filtered.filter((wo) => {
        const value = wo.grossValue;
        const min = filters.valueRange.min
          ? parseFloat(filters.valueRange.min)
          : 0;
        const max = filters.valueRange.max
          ? parseFloat(filters.valueRange.max)
          : Infinity;
        return value >= min && value <= max;
      });
    }
    if (filters?.dateRange?.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter((wo) => {
        const requestDate = dayjs(wo.changeRequestDate);
        return requestDate.isAfter(startDate) && requestDate.isBefore(endDate);
      });
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      site: "",
      project: "",
      status: "",
      contractType: "",
      contractor: "",
      valueRange: { min: "", max: "" },
      dateRange: null,
      globalSearch: "",
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.site) count++;
    if (filters.project) count++;
    if (filters.status) count++;
    if (filters.contractType) count++;
    if (filters.contractor) count++;
    if (filters.valueRange.min || filters.valueRange.max) count++;
    if (filters.dateRange?.length === 2) count++;
    if (filters.globalSearch) count++;
    return count;
  };

  const saveCurrentFilter = () => {
    const filterName = `Filter ${savedFilters.length + 1}`;
    const newFilter = {
      id: Date.now(),
      name: filterName,
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };
    setSavedFilters((prev) => [...prev, newFilter]);
    message.success("Filter saved successfully");
  };

  const applySavedFilter = (savedFilter: any) => {
    setFilters(savedFilter.filters);
    message.success(`Applied saved filter: ${savedFilter.name}`);
  };

  const deleteSavedFilter = (filterId: number) => {
    setSavedFilters((prev) => prev.filter((f) => f.id !== filterId));
    message.success("Filter deleted successfully");
  };

  const calculateAge = (requestDate: string) => {
    return dayjs().diff(dayjs(requestDate), "days");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewAmendment = (record: any) => {
    navigate(`/work-order-view/${record.workOrderId}`);
  };

  const handleEditAmendment = (record: WorkOrderAmendmentData) => {
    navigate(`/work-order-creation/?edit=${record.id}`);
  };

  // const exportFilteredData = () => {
  //   const csvContent = "data:text/csv;charset=utf-8," +
  //     "WO Number,Title,Site,Project,Contractor,Value,Status,Request Date,Age,Requested By\n" +
  //     filteredData.map(wo =>
  //       `${wo.workOrderNumber},"${wo.workOrderTitle}",${wo.siteName},${wo.projectName},${wo.contractorName},${wo.grossValue},${wo.changeRequestStatus},${wo.changeRequestDate},${calculateAge(wo.changeRequestDate)},${wo.changeRequestedBy}`
  //     ).join("\n");

  //   const encodedUri = encodeURI(csvContent);
  //   const link = document.createElement("a");
  //   link.setAttribute("href", encodedUri);
  //   link.setAttribute("download", `work_order_amendments_${dayjs().format('YYYY-MM-DD')}.csv`);
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   message.success('Data exported successfully');
  // };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          onClick={() => handleViewAmendment(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 180,
      render: (_: any, record: WorkOrderAmendmentData) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.siteName}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.projectName}
          </div>
        </div>
      ),
    },
    {
      title: "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 120,
      render: (value: number) => (
        <span style={{ fontWeight: 500 }}>{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: ChangeRequestStatus) => (
        <Tag color={getChangeRequestStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Requested By",
      dataIndex: "changeRequestedBy",
      key: "changeRequestedBy",
      width: 150,
    },
    {
      title: "Request Date",
      dataIndex: "changeRequestDate",
      key: "changeRequestDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Age (Days)",
      key: "age",
      width: 100,
      render: (_: any, record: WorkOrderAmendmentData) => {
        const age = calculateAge(record.changeRequestDate);
        return (
          <span
            style={{
              color: age > 7 ? "#ff4d4f" : age > 3 ? "#faad14" : "#52c41a",
              fontWeight: 500,
            }}
          >
            {age}
          </span>
        );
      },
    },
    // {
    //   title: "Comments",
    //   key: "commentsCount",
    //   width: 100,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Badge count={record.changeRequestComments.length} showZero>
    //       <Button type="text" size="small">💬</Button>
    //     </Badge>
    //   ),
    // },
    // {
    //   title: "Files",
    //   key: "filesCount",
    //   width: 80,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Badge count={record.changeRequestFiles.length} showZero>
    //       <Button type="text" size="small">📎</Button>
    //     </Badge>
    //   ),
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   width: 150,
    //   fixed: 'right' as const,
    //   render: (_: any, record: WorkOrderAmendmentData) => (
    //     <Space>
    //       <Tooltip title="View Details">
    //         <Button
    //           type="primary"
    //           ghost
    //           size="small"
    //           icon={<EyeOutlined />}
    //           onClick={() => handleViewAmendment(record)}
    //         />
    //       </Tooltip>
    //       <Tooltip title="Review & Edit">
    //         <Button
    //           type="primary"
    //           size="small"
    //           icon={<EditOutlined />}
    //           onClick={() => handleEditAmendment(record)}
    //         />
    //       </Tooltip>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="work-order-contractor-approvals"
        title="Work Order Amendment"
        columns={columns}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1400,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Change Request date",
        }}
        // widget={{
        //   isActive: true,
        //   compactGrid: true,
        //   kpiFilter: {
        //     isActive: true,
        //     options: kpiFilterOptions,
        //     selectedFilter: selectedKPIFilter,
        //     onFilterChange: handleKPIFilterChange,
        //   },
        //   list: getWidgetsByFilter(selectedKPIFilter),
        // }}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
      />

      {/* Advanced Filter Drawer */}
      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              <FilterOutlined style={{ marginRight: 8 }} />
              Filters
            </span>
            <Badge count={getActiveFilterCount()} showZero={false} />
          </div>
        }
        placement="right"
        width={420}
        open={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        extra={
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={saveCurrentFilter}
              disabled={getActiveFilterCount() === 0}
            >
              Save Filter
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={clearFilters}
              disabled={getActiveFilterCount() === 0}
            >
              Reset
            </Button>
          </Space>
        }
      >
        <div style={{ padding: "0 0 16px 0" }}>
          {/* Saved Filters */}
          {savedFilters.length > 0 && (
            <>
              <div style={{ marginBottom: 24 }}>
                <Title level={5} style={{ marginBottom: 12 }}>
                  Saved Filters
                </Title>
                <div style={{ maxHeight: 120, overflowY: "auto" }}>
                  {savedFilters.map((savedFilter) => (
                    <div
                      key={savedFilter.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 0",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Button
                        type="link"
                        size="small"
                        onClick={() => applySavedFilter(savedFilter)}
                        style={{
                          padding: 0,
                          height: "auto",
                          textAlign: "left",
                        }}
                      >
                        {savedFilter.name}
                      </Button>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => deleteSavedFilter(savedFilter.id)}
                        danger
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Divider />
            </>
          )}

          {/* Filter Fields */}
          <div className="filter-section">
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Site
              </Text>
              <Select
                placeholder="Select Site"
                value={filters.site}
                onChange={(value) => handleFilterChange("site", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {mockSiteOptions.map((site) => (
                  <Option key={site.value} value={site.value}>
                    {site.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Project
              </Text>
              <Select
                placeholder="Select Project"
                value={filters.project}
                onChange={(value) => handleFilterChange("project", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {mockProjectOptions.map((project) => (
                  <Option key={project.value} value={project.value}>
                    {project.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Change Status
              </Text>
              <Select
                placeholder="Select Status"
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                style={{ width: "100%" }}
                allowClear
              >
                <Option value={ChangeRequestStatus.ChangeRequested}>
                  Change Requested
                </Option>
                <Option value={ChangeRequestStatus.UnderReview}>
                  Under Review
                </Option>
                <Option value={ChangeRequestStatus.AmendmentApproved}>
                  Amendment Approved
                </Option>
                <Option value={ChangeRequestStatus.AmendmentRejected}>
                  Amendment Rejected
                </Option>
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Contract Type
              </Text>
              <Select
                placeholder="Select Contract Type"
                value={filters.contractType}
                onChange={(value) => handleFilterChange("contractType", value)}
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {mockContractTypeOptions.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Contractor
              </Text>
              <Input
                placeholder="Enter Contractor name"
                value={filters.contractor}
                onChange={(e) =>
                  handleFilterChange("contractor", e.target.value)
                }
                allowClear
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                Request Date Range
              </Text>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
                placeholder={["Start Date", "End Date"]}
                format="DD/MM/YYYY"
              />
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default WorkOrderAmendments;
